import { useEffect, useState } from "react";
import { api } from "../../../../lib/api";
import SearchableSelect from "./SearchableSelect";

/**
 * Searchable active-vendor picker (value = vendor id) used wherever a screen
 * explicitly asks for "Vendor" rather than a generic account - Vendor
 * Payment, Purchase / Vendor Bill, Credit Note, Debit Note, and the
 * "Add" shortcut in Journal Voucher. The backend resolves the chosen vendor
 * to its linkedLedgerId itself.
 *
 * Phase 5 "Searchable Selectors" correction: search matches Vendor Name,
 * Contact Person, Mobile and GSTIN - not just the visible name - and shows
 * the contact/mobile/GSTIN as sub-text in results so two similarly-named
 * vendors are never ambiguous.
 *
 * Phase 5 "Fix Payee/Party UX and Payable Linkage" correction: added the
 * optional `onSelectVendor(vendorObject)` callback so this same searchable
 * list can be reused purely as a name-autocomplete for a "Paid To / Payee"
 * field elsewhere in a form (Party Type = Vendor) without creating a second,
 * conflicting vendorId reference on a voucher line whose ledgerId/vendorId
 * slot is already used for the main Payable Account selection - the caller
 * captures vendor.vendorName into a free-text partyName instead. Existing
 * callers that only pass onChange are unaffected.
 */
export default function VendorSelect({ value, onChange, onSelectVendor, placeholder = "Search vendor...", disabled, clearable = false, extraVendors = [] }) {
  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/society/accounting/engine/vendors").catch(() => ({ data: [] }));
        setVendors(Array.isArray(res.data) ? res.data : []);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  // Phase 15 CORRECTION (Tally-like Purchase / Vendor Bill), item 1: when a
  // caller quick-creates a Vendor inline (without leaving the voucher, via
  // its own "+ Create Vendor" dialog rather than the Vendors master page),
  // the new vendor is not yet in this component's own one-time-fetched
  // list. `extraVendors` lets that caller merge it in immediately so the
  // just-created vendor's name displays correctly right away, instead of
  // showing blank/placeholder until the page is reloaded. Defaults to `[]`
  // so every existing caller is completely unaffected.
  const allVendors = extraVendors.length ? [...vendors, ...extraVendors.filter((v) => !vendors.some((existing) => existing.id === v.id))] : vendors;

  const options = allVendors.map((v) => ({
    value: v.id,
    label: v.vendorName,
    sublabel: [v.contactPerson, v.mobile, v.gstin ? `GSTIN: ${v.gstin}` : ""].filter(Boolean).join(" · ") || undefined,
    keywords: [v.contactPerson, v.mobile, v.gstin],
  }));

  const handleChange = (id) => {
    onChange?.(id);
    if (onSelectVendor) {
      const picked = vendors.find((v) => v.id === id) || null;
      onSelectVendor(picked);
    }
  };

  return (
    <SearchableSelect
      value={value}
      onChange={handleChange}
      options={options}
      loading={loading}
      disabled={disabled}
      placeholder={placeholder}
      searchPlaceholder="Search by vendor name, contact, mobile or GSTIN..."
      emptyText="No vendors match. Set one up under Vendors master."
      clearable={clearable}
    />
  );
}
